import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Spinner,
  Switch,
} from '@fluentui/react-components'
import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { getProjectSubscriptions } from '../../common/src/selectors/projectSubscriptionsSelectors'
import { useDispatch } from 'react-redux'
import { postProjectSubscriptions } from '../../common/src/actions/projectSubscriptionsAPI'
import { useTranslation } from 'react-i18next'
import { getProjectSubscriptionsIfNotPending } from '../../common/src/actions/combinedAPI.js'

const usePostSubscriptions = ({ projectId }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const dispatch = useDispatch()
  const postSubscriptions = useCallback(
    async subscriptions => {
      setLoading(true)
      const response = await dispatch(postProjectSubscriptions(projectId, subscriptions))
      setLoading(false)
      if (response.error) {
        setError(response.error)
      } else {
        setSuccess(true)
      }
      return response
    },
    [dispatch, projectId]
  )
  useEffect(() => {
    setSuccess(false)
    setError(null)
  }, [projectId])

  return { loading, error, success, postSubscriptions }
}

export const ProjectSubscriptionsModal = ({ projectId, isOpen, onSave, onClose }) => {
  const dispatch = useDispatch()
  const subscriptions = useSelector(getProjectSubscriptions(projectId))
  const [subscriptionsState, setSubscriptionsState] = useState(subscriptions)
  const { loading: postLoading, error, postSubscriptions } = usePostSubscriptions({ projectId })
  const { t } = useTranslation()
  const loading = postLoading || subscriptions === undefined

  useEffect(() => {
    if (isOpen && !subscriptions) {
      dispatch(getProjectSubscriptionsIfNotPending({ projectID: projectId }))
    }
  }, [dispatch, isOpen, projectId, subscriptions])

  useEffect(() => {
    setSubscriptionsState(subscriptions)
  }, [projectId, subscriptions])

  const toggles = subscriptionsState
    ?.map((value, key) => {
      return (
        <Switch
          key={key}
          checked={value}
          label={t('project_subscriptions_modal.toggle_labels.' + key)}
          onChange={(evt, data) => {
            setSubscriptionsState(prev => prev.set(key, data.checked))
          }}
          disabled={loading}
        />
      )
    })
    .toArray()

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(ev, data) => {
        if (!data.open) onClose()
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle className="flex gap-4">
            {t('project_subscriptions_modal.title')} {loading && <Spinner size="small" />}
          </DialogTitle>
          <DialogContent>
            <span>{t('project_subscriptions_modal.subtitle')}</span>
            <div className="mx-auto mt-2 flex w-fit flex-col">{toggles}</div>
            {error && <span className="text-pm-negative">{t('project_subscriptions_modal.error')}</span>}
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" onClick={onClose} disabled={loading}>
                {t('general.close')}
              </Button>
            </DialogTrigger>
            <Button
              appearance="primary"
              onClick={async () => {
                const response = await postSubscriptions(subscriptionsState)
                if (!response.error) {
                  onSave?.()
                }
              }}
              disabled={subscriptions?.equals(subscriptionsState) || loading}
            >
              {t('general.save')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
